// ScopeDetailPage.tsx — manage a single scope: description metadata + the
// roles that grant it. The scope itself stays implicit in role permissions
// (adding/removing PATCHes the underlying role); a separate description row
// is editable independently and persists in identity_scopes (mirrored back
// into any matching AuthZScope CR by the server).
import { useState, useEffect, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import NameTag from '../components/NameTag'
import Combobox from 'common/ui/Combobox'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import { BsPlusLg, BsTrash } from 'react-icons/bs'
import { useAuth, apiFetch } from '../hooks/useAuth'

interface RoleData {
    name: string
    description?: string
    permissions: string[]
}

const ScopeDetailPage = () => {
    const { name = '' } = useParams<{ name: string }>()
    const { email, loading, error: authError } = useAuth()
    const [allRoles, setAllRoles] = useState<RoleData[]>([])
    const [addRole, setAddRole] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [fetchErr, setFetchErr] = useState('')
    const [description, setDescription] = useState('')
    const [descLoaded, setDescLoaded] = useState(false)
    const [descSaving, setDescSaving] = useState(false)

    const fetchAll = useCallback(async () => {
        setFetchErr('')
        try {
            const [rolesRes, scopeRes] = await Promise.all([
                apiFetch('/api/admin/roles'),
                apiFetch(`/api/admin/scopes/${encodeURIComponent(name)}`),
            ])
            if (!rolesRes.ok) { setFetchErr('Failed to load roles'); return }
            const rolesData = await rolesRes.json()
            setAllRoles(rolesData.roles || [])
            if (scopeRes.ok) {
                const scopeData = await scopeRes.json()
                setDescription(scopeData.description || '')
            } else {
                // 404 = no metadata row yet; that's fine, leave blank.
                setDescription('')
            }
            setDescLoaded(true)
        } catch {
            setFetchErr('Failed to load roles')
        }
    }, [name])

    useEffect(() => {
        if (!loading && email) fetchAll()
    }, [loading, email, fetchAll])

    const patchRole = async (role: RoleData, permissions: string[]) => {
        const res = await apiFetch(`/api/admin/roles/${encodeURIComponent(role.name)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ permissions }),
        })
        if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            throw new Error(data.error || `Failed to update ${role.name}`)
        }
    }

    const grantingRoles = allRoles.filter((r) => (r.permissions || []).includes(name))
    const remainingRoles = allRoles.filter((r) => !(r.permissions || []).includes(name))

    const handleRemove = async (role: RoleData) => {
        setError(''); setSuccess('')
        try {
            await patchRole(role, (role.permissions || []).filter((p) => p !== name))
            setSuccess(`Removed from ${role.name}`)
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to remove')
        }
    }

    const handleSaveDescription = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(''); setSuccess('')
        setDescSaving(true)
        try {
            const res = await apiFetch(`/api/admin/scopes/${encodeURIComponent(name)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ description }),
            })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || 'Failed to save description')
            }
            setSuccess('Description saved')
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save description')
        } finally {
            setDescSaving(false)
        }
    }

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(''); setSuccess('')
        const target = addRole.trim()
        if (!target) return
        const role = allRoles.find((r) => r.name === target)
        if (!role) { setError(`Role "${target}" not found`); return }
        if ((role.permissions || []).includes(name)) {
            setError(`${role.name} already grants ${name}`)
            return
        }
        try {
            await patchRole(role, [...(role.permissions || []), name])
            setSuccess(`Added to ${role.name}`)
            setAddRole('')
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to add role')
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (fetchErr) {
        return (
            <>
                <h1 className="mb-4">Scope</h1>
                <Alert variant="danger">{fetchErr}</Alert>
                <Link to="/scopes">← Back to scopes</Link>
            </>
        )
    }

    return (
        <>
            <Stack direction="horizontal" className="mb-4 align-items-center">
                <h1 className="m-0 d-flex align-items-center gap-2">
                    <Link to="/scopes" className="text-muted text-decoration-none">Scopes</Link>
                    <span className="text-muted">/</span>
                    <NameTag name={name} kind="scope" />
                </h1>
            </Stack>

            {authError && <Alert variant="danger">{authError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Header>Description</Card.Header>
                <Card.Body>
                    <Form id="save-description-form" onSubmit={handleSaveDescription}>
                        <Form.Group>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Describe what this scope grants — shown to admins assigning roles."
                                disabled={!descLoaded}
                            />
                        </Form.Group>
                    </Form>
                    <small className="text-muted d-block mt-2">
                        Saved to the authz DB and mirrored back into any AuthZScope CR
                        with this name, so frontend edits persist across the next reconcile.
                    </small>
                </Card.Body>
                <Card.Footer>
                    <Button
                        variant="success"
                        type="submit"
                        form="save-description-form"
                        disabled={!descLoaded || descSaving}
                        className="d-flex align-items-center gap-2"
                    >
                        Save description
                    </Button>
                </Card.Footer>
            </Card>

            <Card className="mb-3">
                <Card.Header>Granted by ({grantingRoles.length})</Card.Header>
                <Card.Body>
                    {grantingRoles.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {grantingRoles.map((r) => (
                                <NameTag
                                    key={r.name}
                                    name={r.name}
                                    kind="role"
                                    to={`/roles/${encodeURIComponent(r.name)}`}
                                    actions={[{ icon: <BsTrash />, onClick: () => handleRemove(r), label: `Remove ${name} from ${r.name}`, tone: 'danger' }]}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted small">
                            No role grants this scope. Scopes that aren't granted by any role aren't
                            persisted; add it to a role below to keep it.
                        </p>
                    )}
                    <Form id="add-role-form" onSubmit={handleAdd}>
                        <Form.Group>
                            <Form.Label>Grant to role</Form.Label>
                            <Combobox
                                value={addRole}
                                onChange={setAddRole}
                                options={remainingRoles.map((r) => r.name)}
                                style={{ width: "100%" }}
                                placeholder="role.name (autocompletes from existing roles)"
                            />
                        </Form.Group>
                    </Form>
                </Card.Body>
                <Card.Footer>
                    <Button variant="success" type="submit" form="add-role-form" disabled={!addRole.trim()} className="d-flex align-items-center gap-2">
                        <BsPlusLg /> Add
                    </Button>
                </Card.Footer>
            </Card>
        </>
    )
}

export default ScopeDetailPage
